import { useEffect } from 'react';

import { SeoCategory } from '~/src/common/components/Seo/SeoCategory';
import ErrorLarge from '~/src/common/components/States/Error/Large';
import LoadingLarge from '~/src/common/components/States/Loading/Large';
import I18n from '~/src/common/services/I18n';
import { useGetCategory } from '~/src/queries/api-ecom/generated/api-ecom';

import Category from '../Category/Category';
import Menu from '../Menu/Menu';
import { useCategoryAnalytics } from '../use-category-analytics';

import styles from './Page.module.scss';
import ScreenLayout from './ScreenLayout';

interface Props {
  slug: string;
}

const CategoryPage = ({ slug }: Props) => {
  // Chargement de la catégorie
  const { data, refetch, isError } = useGetCategory(slug);

  // gestion des analytics
  useCategoryAnalytics(data);

  // Conditionnement de l'affichage
  const isLoaded = data != null;
  const isFailure = !isLoaded && isError;
  const isWaiting = !isLoaded && !isFailure;

  // Pour contourner le fait que les ancres ne sont évaluées qu'au changement d'url
  // et que le contenu de la page n'est pas forcément disponible à ce moment, on
  // force le navigateur à ré-évaluer les ancres au changement de contenu actif
  const isActiveSlug = slug === data?.slug;
  useEffect(() => {
    if (!document.location.hash) return;
    const selector = document.location.hash.slice(1);
    document.getElementById(selector)?.scrollIntoView();
  }, [isActiveSlug]);

  return (
    <ScreenLayout>
      <>
        {data ? <SeoCategory category={data} /> : null}
        <div className={styles.container}>
          <div className={styles.head} />
          <aside className={styles.side}>
            <Menu slug={slug} />
          </aside>
          <main className={styles.body}>
            {isLoaded ? <Category category={data} /> : null}
            {isFailure ? (
              <ErrorLarge
                text={I18n.t('category.error.large.text')}
                button={I18n.t('category.error.large.btn')}
                onClick={refetch}
              />
            ) : null}
            {isWaiting ? <LoadingLarge /> : null}
          </main>
        </div>
      </>
    </ScreenLayout>
  );
};

export default CategoryPage;
